/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Upload, Trash2, Megaphone, ImageIcon, Eye } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
export const abs = (u) => (!u ? "" : u.startsWith("http") || u.startsWith("blob:") || u.startsWith("data:") ? u : `${BACKEND}${u}`);

let cache = null;
const previewTarget = () => new URLSearchParams(window.location.search).get("bg_preview");
// Public config; with ?bg_preview=<target> (CEO preview) the unapplied draft replaces that page's background
export const usePageVisuals = () => {
  const [v, setV] = useState(previewTarget() ? null : cache);
  useEffect(() => {
    axios.get(`${API}/public/page-visuals`).then((r) => {
      const pt = previewTarget(), d = r.data;
      if (pt && d[pt]?.draft) { setV({ ...d, [pt]: { ...d[pt], ...d[pt].draft } }); return; }
      cache = d; setV(d);
    }).catch(() => {});
  }, []);
  return v;
};

const bgFilter = (c) => `brightness(${(c?.brightness ?? 100) / 100}) contrast(${(c?.contrast ?? 100) / 100})`;

// Framed image (position + zoom chosen by the CEO), filled edge to edge
const FramedImage = ({ cfg }) => {
  const x = cfg.pos_x ?? 50, y = cfg.pos_y ?? 50, z = (cfg.zoom ?? 100) / 100;
  return (
    <img src={abs(cfg.url)} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
      style={{ objectPosition: `${x}% ${y}%`, transform: `scale(${z})`, transformOrigin: `${x}% ${y}%`, filter: bgFilter(cfg) }} />
  );
};

// Whole-page background: fixed behind all content, full width and height of the screen
export const PageBackground = ({ cfg, testid, contained = false }) => !cfg?.url ? null : (
  <div className={`${contained ? "absolute" : "fixed"} inset-0 z-0 overflow-hidden pointer-events-none`} data-testid={testid}>
    <FramedImage cfg={cfg} />
    <div className="absolute inset-0 bg-black" style={{ opacity: (cfg.overlay ?? 40) / 100 }} />
  </div>
);

// Small ad square under the section cards — mounted after the main content has painted
export const SectionAd = ({ ad }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 800);
    return () => clearTimeout(t);
  }, []);
  if (!ad?.enabled || !ad?.url || !ready) return null;
  const media = ad.type === "video"
    ? <video src={abs(ad.url)} autoPlay muted loop playsInline preload="metadata" className="w-full h-full object-cover" data-testid="section-ad-video" />
    : <img src={abs(ad.url)} alt="Publicité" loading="lazy" decoding="async" className="w-full h-full object-cover" data-testid="section-ad-image" />;
  return (
    <div className="mt-5 mx-auto w-full max-w-[260px] animate-fade-slide" data-testid="section-ad">
      <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-1 text-center">Publicité</p>
      <div className="aspect-square rounded-2xl overflow-hidden border border-zinc-700 bg-zinc-900 shadow-lg">
        {ad.link ? <a href={ad.link} target="_blank" rel="noopener noreferrer" data-testid="section-ad-link">{media}</a> : media}
      </div>
    </div>
  );
};

// ---------- CEO side ----------
const CHUNK = 4 * 1024 * 1024;
const uploadVisual = async (target, f, onProgress) => {
  const total = Math.max(1, Math.ceil(f.size / CHUNK));
  const uid = Array.from(crypto.getRandomValues(new Uint8Array(12))).map((b) => b.toString(16).padStart(2, "0")).join("");
  let res;
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uid); fd.append("index", i); fd.append("total", total); fd.append("mime", f.type);
    fd.append("file", f.slice(i * CHUNK, (i + 1) * CHUNK), f.name);
    res = await axios.post(`${API}/ceo/page-visuals/${target}/chunk`, fd);
    onProgress(Math.round(((i + 1) / total) * 100));
  }
  return res.data;
};

const Slider = ({ label, value, min, max, onChange, id, unit = "%" }) => (
  <label className="block">
    <span className="flex justify-between text-xs text-zinc-300 mb-1"><span>{label}</span><span className="text-amber-400 font-black">{value}{unit}</span></span>
    <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(parseInt(e.target.value, 10))} className="w-full accent-amber-400" data-testid={id} />
  </label>
);

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const LOOK_KEYS = ["brightness", "contrast", "overlay", "pos_x", "pos_y", "zoom"];
const pickLook = (c) => ({ brightness: 100, contrast: 100, overlay: 40, pos_x: 50, pos_y: 50, zoom: 100, ...Object.fromEntries(LOOK_KEYS.filter((k) => c?.[k] != null).map((k) => [k, c[k]])) });

// Drag with a finger/mouse to move the image, pinch / wheel / slider to zoom. Same screen shape as the public page.
const FramingArea = ({ url, look, setLook, target }) => {
  const box = useRef(), pts = useRef(new Map()), pinch = useRef(null);
  const [device, setDevice] = useState("phone");
  const ratio = device === "phone" ? 390 / 844 : 16 / 9;
  useEffect(() => {
    const el = box.current; if (!el) return;
    const onWheel = (e) => { e.preventDefault(); setLook((l) => ({ ...l, zoom: Math.round(clamp(l.zoom - e.deltaY * 0.2, 100, 400)) })); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [device]);
  const move = (dx, dy) => {
    const el = box.current; if (!el) return;
    const z = look.zoom / 100;
    setLook((l) => ({ ...l, pos_x: clamp(l.pos_x - (dx / el.clientWidth) * 100 / z, 0, 100), pos_y: clamp(l.pos_y - (dy / el.clientHeight) * 100 / z, 0, 100) }));
  };
  const down = (e) => { box.current.setPointerCapture?.(e.pointerId); pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY }); };
  const up = (e) => { pts.current.delete(e.pointerId); pinch.current = null; };
  const pmove = (e) => {
    const prev = pts.current.get(e.pointerId); if (!prev) return;
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pts.current.size === 2) {
      const [p1, p2] = [...pts.current.values()];
      const d = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      if (pinch.current) setLook((l) => ({ ...l, zoom: Math.round(clamp(l.zoom * (d / pinch.current), 100, 400)) }));
      pinch.current = d;
    } else move(e.clientX - prev.x, e.clientY - prev.y);
  };
  return (
    <div>
    <div className="flex justify-center gap-1 mb-2">
      {[["phone", "Téléphone"], ["desktop", "Ordinateur"]].map(([k, l]) => (
        <button key={k} onClick={() => setDevice(k)} className={`text-[11px] font-bold rounded-full px-3 py-1 ${device === k ? "bg-amber-400 text-black" : "bg-zinc-800 text-zinc-300"}`} data-testid={`bg-device-${k}-${target}`}>{l}</button>
      ))}
    </div>
    <div ref={box} onPointerDown={down} onPointerMove={pmove} onPointerUp={up} onPointerCancel={up}
      className="relative mx-auto rounded-xl overflow-hidden bg-zinc-950 border-2 border-amber-500/60 cursor-grab active:cursor-grabbing touch-none"
      style={{ aspectRatio: `${ratio}`, maxHeight: 360, width: ratio < 1 ? "auto" : "100%", height: ratio < 1 ? 360 : "auto" }} data-testid={`bg-frame-${target}`}>
      {url ? <PageBackground cfg={{ ...look, url }} contained /> : <div className="absolute inset-0 vape-bg" />}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-3 pointer-events-none">
        <p className="text-white font-black text-lg">VAPE YARD</p>
        <p className="text-zinc-200 text-[11px]">Glissez pour placer · pincez pour zoomer</p>
      </div>
    </div>
    </div>
  );
};

// Real-size preview: the actual public page in full screen, showing the unapplied draft
const FullPreview = ({ target, onClose, onApply }) => createPortal(
  <div className="fixed inset-0 z-[200] bg-black" data-testid={`bg-fullpreview-${target}`}>
    <iframe title="Aperçu" src={`${window.location.origin}${target === "welcome" ? "/" : "/login/haiti"}?bg_preview=${target}`} className="w-full h-full border-0" />
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/85 backdrop-blur rounded-full p-1.5 shadow-2xl border border-amber-500/50 whitespace-nowrap">
      <span className="text-amber-400 text-xs font-black px-2">APERÇU</span>
      <button onClick={onClose} className="bg-zinc-800 text-white text-xs font-bold rounded-full px-4 py-2" data-testid="bg-fullpreview-close">Fermer</button>
      <button onClick={onApply} className="bg-amber-400 text-black text-xs font-black rounded-full px-4 py-2" data-testid="bg-fullpreview-apply">Appliquer</button>
    </div>
  </div>, document.body
);

const BgEditor = ({ target, title, cfg, onSaved }) => {
  const [look, setLook] = useState(pickLook());
  const [draftUrl, setDraftUrl] = useState(null);
  const [progress, setProgress] = useState(null);
  const [preview, setPreview] = useState(false);
  const ref = useRef();
  useEffect(() => { if (cfg) { setLook(pickLook(cfg.draft || cfg)); setDraftUrl(cfg.draft?.url && cfg.draft.url !== cfg.url ? cfg.draft.url : null); } }, [cfg]);
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Choisissez une image");
    if (f.size > 15 * 1024 * 1024) return toast.error("Image trop grosse (max 15 Mo)");
    setProgress(0);
    try { const r = await uploadVisual(target, f, setProgress); setDraftUrl(r.url); setLook((l) => ({ ...l, pos_x: 50, pos_y: 50, zoom: 100 })); toast.success("Image chargée — placez-la, puis Aperçu ou Appliquer"); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
    finally { setProgress(null); }
  };
  const saveDraft = () => axios.put(`${API}/ceo/page-visuals/${target}/draft`, look);
  const openPreview = async () => { try { await saveDraft(); setPreview(true); } catch { toast.error("Erreur"); } };
  const apply = async () => {
    try {
      await saveDraft();
      await axios.post(`${API}/ceo/page-visuals/${target}/apply`);
      toast.success("Fond appliqué — visible par le public");
      setPreview(false); setDraftUrl(null); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async () => {
    if (!window.confirm("Retirer l'image de fond ?")) return;
    await axios.put(`${API}/ceo/page-visuals/${target}`, { ...look, remove_media: true });
    toast.success("Fond retiré"); onSaved();
  };
  const url = draftUrl || cfg?.url;
  const set = (k) => (v) => setLook((l) => ({ ...l, [k]: v }));
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`bg-editor-${target}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-white font-black">{title}</p>
        {draftUrl && <span className="text-[10px] font-black bg-amber-400 text-black rounded-full px-2 py-0.5" data-testid={`bg-draft-badge-${target}`}>NON APPLIQUÉ</span>}
      </div>
      <div className="mb-3" data-testid={`bg-preview-${target}`}><FramingArea url={url} look={look} setLook={setLook} target={target} /></div>
      <div className="space-y-2 mb-3">
        <Slider label="Zoom" value={look.zoom} min={100} max={400} onChange={set("zoom")} id={`bg-zoom-${target}`} />
        <Slider label="Luminosité" value={look.brightness} min={20} max={180} onChange={set("brightness")} id={`bg-brightness-${target}`} />
        <Slider label="Contraste" value={look.contrast} min={40} max={180} onChange={set("contrast")} id={`bg-contrast-${target}`} />
        <Slider label="Voile sombre (lisibilité du texte)" value={look.overlay} min={0} max={90} onChange={set("overlay")} id={`bg-overlay-${target}`} />
      </div>
      <input ref={ref} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`bg-input-${target}`} />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => ref.current?.click()} disabled={progress !== null} className="bg-zinc-800 hover:bg-zinc-700 text-amber-400 text-sm font-bold rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid={`bg-pick-${target}`}><Upload className="w-4 h-4" />{progress !== null ? `${progress}%` : url ? "Changer l'image" : "Ajouter une image"}</button>
        <button onClick={openPreview} disabled={!url} className="bg-white hover:bg-zinc-200 disabled:opacity-40 text-black text-sm font-black rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid={`bg-fullpreview-btn-${target}`}><Eye className="w-4 h-4" />Aperçu</button>
        <button onClick={apply} disabled={!url} className="bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-black text-sm font-black rounded-lg px-4 py-2" data-testid={`bg-apply-${target}`}>Appliquer</button>
        <button onClick={() => setLook((l) => ({ ...l, pos_x: 50, pos_y: 50, zoom: 100 }))} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-bold rounded-lg px-3 py-2" data-testid={`bg-recenter-${target}`}>Recentrer</button>
        {cfg?.url && <button onClick={remove} className="bg-red-600/20 hover:bg-red-600/30 text-red-400 text-sm font-bold rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid={`bg-remove-${target}`}><Trash2 className="w-4 h-4" />Retirer</button>}
      </div>
      <p className="text-zinc-500 text-[11px] mt-2">Rien ne change pour le public tant que vous n'avez pas cliqué « Appliquer ».</p>
      {preview && <FullPreview target={target} onClose={() => setPreview(false)} onApply={apply} />}
    </div>
  );
};

const AdEditor = ({ ad, onSaved }) => {
  const [link, setLink] = useState("");
  const [progress, setProgress] = useState(null);
  const ref = useRef();
  useEffect(() => { setLink(ad?.link || ""); }, [ad]);
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const isVideo = f.type.startsWith("video/");
    if (f.size > (isVideo ? 20 : 15) * 1024 * 1024) return toast.error(`Fichier trop gros (max ${isVideo ? 20 : 15} Mo)`);
    setProgress(0);
    try { const r = await uploadVisual("ad", f, setProgress); toast.success(`Publicité mise en ligne (${r.size_kb} Ko)`); onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
    finally { setProgress(null); }
  };
  const save = async (patch) => {
    try { await axios.put(`${API}/ceo/page-visuals/ad`, { enabled: ad?.enabled, link, ...patch }); toast.success("Publicité mise à jour"); onSaved(); }
    catch { toast.error("Erreur"); }
  };
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="ad-editor">
      <div className="flex items-center justify-between mb-3">
        <p className="text-white font-black flex items-center gap-2"><Megaphone className="w-4 h-4 text-amber-400" />Publicité — page de choix de section</p>
        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${ad?.enabled && ad?.url ? "bg-emerald-500 text-black" : "bg-zinc-700 text-zinc-300"}`} data-testid="ad-status">{ad?.enabled && ad?.url ? "ACTIVE" : "DÉSACTIVÉE"}</span>
      </div>
      <div className="flex gap-4 flex-wrap">
        <div className="w-36 aspect-square rounded-xl overflow-hidden bg-zinc-950 border border-zinc-700 flex items-center justify-center" data-testid="ad-preview">
          {ad?.url ? (ad.type === "video" ? <video src={abs(ad.url)} muted autoPlay loop playsInline className="w-full h-full object-cover" /> : <img src={abs(ad.url)} alt="" className="w-full h-full object-cover" />) : <ImageIcon className="w-8 h-8 text-zinc-700" />}
        </div>
        <div className="flex-1 min-w-[200px] space-y-2">
          <input ref={ref} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" onChange={pick} className="hidden" data-testid="ad-input" />
          <button onClick={() => ref.current?.click()} disabled={progress !== null} className="bg-zinc-800 hover:bg-zinc-700 text-amber-400 text-sm font-bold rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="ad-pick"><Upload className="w-4 h-4" />{progress !== null ? `${progress}%` : ad?.url ? "Changer (image / vidéo)" : "Ajouter image / vidéo"}</button>
          <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Lien au clic (optionnel) https://…" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="ad-link-input" />
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => save({})} className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold rounded-lg px-3 py-1.5" data-testid="ad-save-link">Enregistrer le lien</button>
            {ad?.url && <button onClick={() => save({ enabled: !ad.enabled })} className={`text-xs font-black rounded-lg px-3 py-1.5 ${ad.enabled ? "bg-red-600 text-white" : "bg-emerald-500 text-black"}`} data-testid="ad-toggle">{ad.enabled ? "Désactiver" : "Activer"}</button>}
            {ad?.url && <button onClick={() => window.confirm("Supprimer la publicité ?") && save({ remove_media: true })} className="text-xs font-bold rounded-lg px-3 py-1.5 bg-red-600/20 text-red-400" data-testid="ad-remove">Supprimer</button>}
          </div>
          <p className="text-zinc-500 text-[11px]">Images optimisées automatiquement. Vidéo : max 20 Mo, courte et sans son (lecture muette en boucle).</p>
        </div>
      </div>
    </div>
  );
};

export const PageVisualsPanel = () => {
  const [v, setV] = useState(null);
  const load = () => axios.get(`${API}/public/page-visuals`).then((r) => { cache = r.data; setV(r.data); }).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-2xl p-4 space-y-3" data-testid="page-visuals-panel">
      <p className="text-amber-400 font-black text-lg">Fonds des pages publiques & publicité</p>
      <div className="grid md:grid-cols-2 gap-3">
        <BgEditor target="welcome" title="Page d'accueil publique" cfg={v?.welcome} onSaved={load} />
        <BgEditor target="section" title="Page de choix de section" cfg={v?.section} onSaved={load} />
      </div>
      <AdEditor ad={v?.ad} onSaved={load} />
    </div>
  );
};
